import CardSlot from '@gp/components/CardSlot';
import MeetingCard from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MeetingCard/index';
import type { ICardOperationsProps } from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneCard/CardOperations';
import CardOperations from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneCard/CardOperations';
import CardTitle from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneCard/CardTitle';
import '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneCard/index.scss';
import MilestoneForm from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneForm';
import GRNoData from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/NoData/GR';
import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import { getMeetingStatusIcon } from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import type { IBaseProps } from '@home/types/common';
import classnames from 'classnames';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'ahooks';

interface IMilestoneCardProps extends IBaseProps, ICardOperationsProps {
  editable?: boolean;
  milestoneOperationVisible?: boolean;
  detailInfoAuthorityData?: Record<string, boolean>;
  projectDetailData?: Record<string, any>;
  // 是否为nextMilestone
  isNextMilestone?: boolean;
  // 是否展开当前Milestone卡片
  isExpand?: boolean;
  // 是否全部展示Milestones及其子Milestones
  allExpand?: boolean;
  currentMilestoneDate?: string;
  separatedMilestones?: IMilestone[];
  // 自动定位，并展开的milestone的id
  milestoneId?: string;
}

// 是否展开当前Milestone卡片
function useMilestoneCardExpand(
  // 当前milestone是否展开
  initialIsExpand?: boolean,
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  // Milestone是否展开
  const [isExpand, setIsExpand] = useState(!!initialIsExpand);

  useEffect(() => {
    setIsExpand(!!initialIsExpand);
  }, [initialIsExpand]);

  return [isExpand, setIsExpand];
}

const MilestoneCard: React.FC<IMilestoneCardProps> = (props) => {
  const {
    style,
    className,
    editable,
    milestoneOperationVisible = false,
    detailInfoAuthorityData,
    projectDetailData,
    currentMilestoneDate,
    separatedMilestones,
    milestone,
    isExpand: isExpandProps = false,
    allExpand,
    isNextMilestone = false,
    onUpdateMilestone,
    onUpdateGR,
    onDeleteMilestone,
  } = props;

  const { milestone_id: milestoneId, milestone_history: milestoneHistories } =
    milestone || {};
  // 连milestone_history都没有的milestone 判定为空milestone，展示GRNoData，否则展示MilestoneForm
  const isEmptyMilestone =
    !Array.isArray(milestoneHistories) || !milestoneHistories.length;

  // Milestone是否展开
  const [isExpand, setIsExpand] = useMilestoneCardExpand(isExpandProps);

  // 监听总开关阀
  useLayoutEffect(() => {
    setIsExpand(!!allExpand);
  }, [allExpand]);

  useDeepCompareEffect(() => {
    setIsExpand(isExpandProps);
  }, [isExpandProps]);

  const meetingData = useMemo<boolean>(() => {
    return milestone?.milestone_meetings || [];
  }, [milestone]);

  const hasMeetingData = useMemo<boolean>(() => {
    return Array.isArray(meetingData) && meetingData?.length > 0;
  }, [meetingData]);

  return (
    <CardSlot
      style={style}
      className={classnames('dbg-milestone-card', className, {
        expand: isExpand,
        fold: !isExpand,
      })}
      title={
        <CardTitle
          showGRDecisionTag={detailInfoAuthorityData?.milestone_gr}
          // 是否展示NextMilestone的icon
          showNextMilestoneIcon={isNextMilestone}
          milestone={milestone}
          onTitleClick={() => {
            setIsExpand(!isExpand);
          }}
        />
      }
      rightDom={
        editable && (
          <CardOperations
            milestoneOperationVisible={milestoneOperationVisible}
            milestone={milestone}
            onUpdateMilestone={onUpdateMilestone}
            onUpdateGR={onUpdateGR}
            onDeleteMilestone={onDeleteMilestone}
          />
        )
      }
    >
      {/* 新增meeting  S*/}
      {hasMeetingData && (
        <div className="dbg-milestone-card-meeting">
          <div className="dbg-milestone-meeting-div">
            <div className="dbg-milestone-meeting">
              {meetingData?.map((meeting: any, index: number) => {
                const typeText =
                  meeting?.source === 1 ? 'Event on' : 'Meeting on';
                const typeTextShort =
                  meeting?.source === 1 ? 'Event' : 'Meeting';
                const isDisabled = meeting?.status === 1;
                const timeText =
                  meeting?.source === 1
                    ? `${meeting?.start_date}~${meeting?.end_date}`
                    : meeting?.start_date;
                const titleText = isDisabled
                  ? `${typeTextShort} to be scheduled`
                  : `${typeText} ${timeText}`;
                const iconType = meeting?.source === 1 ? 'flag' : 'meeting';
                const statusValue = getMeetingStatusIcon(meeting?.status);
                return (
                  <MeetingCard
                    key={index}
                    disabled={isDisabled}
                    iconType={iconType}
                    dateText={titleText}
                    status={statusValue}
                    content={meeting?.name}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className="dbg-milestone-card-context">
        {isEmptyMilestone && (
          <GRNoData
            editable={editable}
            onUpdateGR={() => {
              if (!milestoneId) return;
              onUpdateGR?.(milestoneId);
            }}
          />
        )}
        {isEmptyMilestone || (
          <MilestoneForm
            isExpand={isExpand}
            detailInfoAuthorityData={detailInfoAuthorityData}
            projectDetailData={projectDetailData}
            currentMilestoneDate={currentMilestoneDate}
            separatedMilestones={separatedMilestones}
            milestone={milestone}
          />
        )}
      </div>
    </CardSlot>
  );
};

export default MilestoneCard;
